import React, { useEffect, useState } from 'react';
import { staffApi, trainingApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Loader2, Lock, LockOpen } from 'lucide-react';
import { toast } from 'sonner';
import StaffCombobox from './StaffCombobox';

export default function UnlockManager({ files, title, description, noun = 'Capitolo', nounPlural = 'capitoli', testPrefix = '' }) {
  const tid = (s) => (testPrefix ? `${testPrefix}-${s}` : s);
  const [staff, setStaff] = useState([]);
  const [staffId, setStaffId] = useState('');
  const [unlocked, setUnlocked] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    staffApi.list().then(r => setStaff(r.data)).catch(() => setStaff([]));
  }, []);

  useEffect(() => {
    if (!staffId) { setUnlocked(null); return; }
    setUnlocked(null);
    trainingApi.unlocks(staffId)
      .then(r => setUnlocked(new Set(r.data.map(u => u.file_id))))
      .catch(() => { toast.error('Errore caricamento sblocchi'); setUnlocked(new Set()); });
  }, [staffId]);

  const toggle = async (fileId, on) => {
    setBusyId(fileId);
    try {
      if (on) {
        await trainingApi.unlock({ staff_id: staffId, file_id: fileId });
        setUnlocked(s => new Set([...s, fileId]));
        toast.success(`${noun} sbloccato`);
      } else {
        await trainingApi.removeUnlock(staffId, fileId);
        setUnlocked(s => { const n = new Set(s); n.delete(fileId); return n; });
        toast.success(`${noun} bloccato`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Operazione fallita');
    } finally {
      setBusyId(null);
    }
  };

  const bulkToggle = async (on) => {
    const targets = on
      ? files.filter(f => !unlocked.has(f.file_id))
      : files.filter(f => unlocked.has(f.file_id));
    if (targets.length === 0) { toast.info(on ? `Tutti i ${nounPlural} sono già sbloccati` : `Nessun ${noun.toLowerCase()} da bloccare`); return; }
    if (!window.confirm(on
      ? `Sbloccare tutti i ${targets.length} ${nounPlural} rimanenti per questo dipendente?`
      : `Bloccare tutti i ${targets.length} ${nounPlural} sbloccati per questo dipendente?`)) return;
    setBulkBusy(true);
    try {
      await Promise.all(targets.map(f => on
        ? trainingApi.unlock({ staff_id: staffId, file_id: f.file_id })
        : trainingApi.removeUnlock(staffId, f.file_id)));
      setUnlocked(prev => {
        const n = new Set(prev);
        targets.forEach(f => on ? n.add(f.file_id) : n.delete(f.file_id));
        return n;
      });
      toast.success(on ? `Tutti i ${nounPlural} sbloccati` : `Tutti i ${nounPlural} bloccati`);
    } catch {
      toast.error('Operazione parzialmente fallita');
      trainingApi.unlocks(staffId).then(r => setUnlocked(new Set(r.data.map(u => u.file_id))));
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 p-4 mb-5" data-testid={tid('unlock-manager')}>
      <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-700 mb-1">
        {title}
      </h3>
      <p className="text-xs text-slate-500 mb-3">{description}</p>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="w-full sm:max-w-md">
          <StaffCombobox staff={staff} value={staffId} onChange={setStaffId}
                         testId={tid('unlock-staff-select')} />
        </div>
        {staffId && unlocked !== null && (
          <>
            <Button variant="outline" size="sm" className="h-9 gap-1.5 rounded-none" disabled={bulkBusy}
                    onClick={() => bulkToggle(true)} data-testid={tid('unlock-all-btn')}>
              {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LockOpen className="w-3.5 h-3.5" />} Sblocca tutti
            </Button>
            <Button variant="outline" size="sm" className="h-9 gap-1.5 rounded-none" disabled={bulkBusy}
                    onClick={() => bulkToggle(false)} data-testid={tid('lock-all-btn')}>
              <Lock className="w-3.5 h-3.5" /> Blocca tutti
            </Button>
          </>
        )}
      </div>

      {staffId && (unlocked === null ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 mt-3">
          {files.map(f => (
            <div key={f.file_id} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-100"
                 data-testid={tid(`unlock-row-${f.file_id}`)}>
              <span className="text-sm text-slate-700">
                {f.chapter_no != null && <span className="text-slate-400 tabular-nums mr-1.5">{String(f.chapter_no).padStart(2, '0')}</span>}
                {f.title}
              </span>
              <div className="flex items-center gap-2">
                {unlocked.has(f.file_id)
                  ? <LockOpen className="w-3.5 h-3.5 text-emerald-500" />
                  : <Lock className="w-3.5 h-3.5 text-slate-300" />}
                <Switch checked={unlocked.has(f.file_id)} disabled={busyId === f.file_id}
                        onCheckedChange={v => toggle(f.file_id, v)}
                        data-testid={tid(`unlock-switch-${f.file_id}`)} />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
